import { createSelector } from '@reduxjs/toolkit';
import { isEmpty } from 'lodash/fp';
import { useSelector } from 'react-redux';

import { getSchemaForFieldPath as getSchemaForFieldPathStoreless } from 'dashboard/components/JsonSchemaSelect/utils';
import { flattenSteps, UsableObjectConfig } from 'dashboard/lib/workflows';
import { generateJsonSchemaConfig, getActualStepObjects } from 'dashboard/lib/workflows/objects';
import { getAllModuleBlockIds } from 'dashboard/lib/workflows/steps-config-operations';
import { WorkflowValidationContext } from 'dashboard/lib/workflows/validation/validationContext';
import { WorkflowBlock } from 'dashboard/models/workflow-block';
import {
  INPUT_STEP_TOKEN,
  outputStep,
  WorkflowStep,
  WorkflowStepInput,
} from 'dashboard/models/workflow-version';
import { workflowBlockEndpoints } from 'dashboard/redux/api/workflow-block';
import { workflowSchemaEndpoints } from 'dashboard/redux/api/workflow-schema';
import { getWorkflowActionMeta } from 'dashboard/redux/selectors/workflow-action-meta';
import { RootState } from 'dashboard/redux/store';
import { JsonSchema } from 'lib/json-schema';
import { buildGraph, findPath } from 'lib/workflows';

const getDashboardApi = (state: RootState) => state.dashboardApi;
export const getIsPlatformObjectJsonSchemaEnabled = (state: RootState) =>
  state.featureFlags?.featureFlags?.['dashboard:workflow-use-platform-object-json-schema'] || false;
export const getIsWorkflowErrorVariablesEnabled = (state: RootState) =>
  state.featureFlags?.featureFlags?.['global:workflow-error-variables'] || false;
export const getIsWorkflowMethodChainV2ValidationEnabled = (state: RootState) =>
  state.featureFlags?.featureFlags?.['dashboard:workflow-method-chain-v2-validation'] || false;
export const getStepContexts = (state: RootState) => state.workflow.present.stepContexts;
const getActiveStepToken = (_: RootState, activeStepToken?: string) => activeStepToken;
const getStepsConfig = (state: RootState) => state.workflow.present.stepsConfig;
export const getInputStep = (state: RootState) => state.workflow.present.inputStep;
const getWorkflowSchemaOld = (state: RootState) => state.workflowSchema;
const getFeatureFlags = (state: RootState) => state.featureFlags.featureFlags;

export const getWorkflowSchema = createSelector(
  [
    getWorkflowSchemaOld,
    getDashboardApi,
    getIsPlatformObjectJsonSchemaEnabled,
    getIsWorkflowMethodChainV2ValidationEnabled,
  ],
  (workflowSchema, dashboardApi, enablePlatformObjectJsonSchema, isMethodChainEnabled) =>
    workflowSchemaEndpoints.getWorkflowSchema.select({
      loadCriteriaVariableSchemas: !enablePlatformObjectJsonSchema,
      loadPrimitives: isMethodChainEnabled,
    })({
      dashboardApi,
    }).data || workflowSchema,
);

const getObjectSchemas = (state: RootState) => state.objectSchemas?.objectSchemaMap;
export const getPlatformObjectJsonSchema = createSelector(
  [getObjectSchemas, getIsPlatformObjectJsonSchemaEnabled],
  (objectSchemas, isPlatformObjectJsonSchemaEnabled) =>
    isPlatformObjectJsonSchemaEnabled && !isEmpty(objectSchemas) ? objectSchemas : undefined,
);

export const usePlatformObjectJsonSchema = () =>
  useSelector((state: RootState) => getPlatformObjectJsonSchema(state));

const getPrimitiveUtilities = (state: RootState) =>
  state.featureFlags?.featureFlags?.['dashboard:workflow-method-chain-v2-validation']
    ? state.workflowSchema.primitives
    : {};

export const usePrimitiveUtilities = () => useSelector(getPrimitiveUtilities);

export const getStepsToProcessCallback = (
  stepsConfig: WorkflowStep[],
  activeStepToken?: string,
) => {
  if (!stepsConfig?.length || activeStepToken === INPUT_STEP_TOKEN) {
    return [];
  }
  if (activeStepToken) {
    return findPath(activeStepToken, [...stepsConfig, outputStep]) || [];
  }
  return flattenSteps(stepsConfig);
};
const getStepsToProcess = createSelector(
  [getStepsConfig, getActiveStepToken],
  getStepsToProcessCallback,
);

export const getUsableObjectsByKeyCallback = (
  stepsToProcess: WorkflowStep[],
  inputStep: WorkflowStepInput,
  stepContexts: Record<string, UsableObjectConfig[]>,
) => {
  if (isEmpty(stepContexts)) {
    return {};
  }

  const usableObjectsByKey: Record<string, UsableObjectConfig[]> = {
    [INPUT_STEP_TOKEN]: stepContexts[INPUT_STEP_TOKEN].map((usableObject) => ({
      ...usableObject,
      step: inputStep,
    })),
  };
  stepsToProcess.forEach((step) => {
    usableObjectsByKey[step.token] = (stepContexts[step.token] || []).map((usableObject) => ({
      ...usableObject,
      step,
    }));
  });

  return usableObjectsByKey;
};
export const getUsableObjectsByKey = createSelector(
  [getStepsToProcess, getInputStep, getStepContexts],
  getUsableObjectsByKeyCallback,
);

export const getUsableObjectsCallback = (
  usableObjectsByKey: Record<string, UsableObjectConfig[]>,
  activeStepToken: string | undefined,
): UsableObjectConfig[] => {
  if (isEmpty(usableObjectsByKey)) {
    return [];
  }
  return getActualStepObjects({
    usableObjectsByKey,
    activeStepToken,
  });
};

export const getUsableObjects = createSelector(
  [getUsableObjectsByKey, getActiveStepToken, getPlatformObjectJsonSchema],
  getUsableObjectsCallback,
);

export const useUsableObjects = (activeNodeToken: string | undefined) =>
  useSelector((state: RootState) => getUsableObjects(state, activeNodeToken));

export const getValidationContext = createSelector(
  [
    getStepContexts,
    getWorkflowSchema,
    getInputStep,
    getStepsConfig,
    getWorkflowActionMeta,
    getFeatureFlags,
    getPlatformObjectJsonSchema,
  ],
  (
    stepContexts,
    workflowSchema,
    inputStep,
    stepsConfig,
    actionMeta,
    featureFlags,
    platformObjectJsonSchema,
  ): WorkflowValidationContext => {
    const getContextForStep = (activeStepToken: string) => {
      const stepsToProcess = getStepsToProcessCallback(stepsConfig, activeStepToken);
      const usableObjectsByKey = getUsableObjectsByKeyCallback(
        stepsToProcess,
        inputStep,
        stepContexts,
      );
      const usableObjects = getUsableObjectsCallback(usableObjectsByKey, activeStepToken);
      const { jsonSchema } = generateJsonSchemaConfig({
        usableObjects,
        useOriginal: true,
      });
      return {
        usableObjects,
        jsonSchema,
      };
    };

    return {
      graph: buildGraph(stepsConfig),
      stepsConfig,
      inputStep,
      workflowSchema,
      actionMeta,
      featureFlags,
      platformObjectJsonSchema,
      getContextForStep,
    };
  },
);

export const getReferencedBlocksById = createSelector(
  [getDashboardApi, getStepsConfig],
  (dashboardApi, stepsConfig): Record<string, WorkflowBlock> => {
    const moduleBlockIds = getAllModuleBlockIds(stepsConfig);
    return moduleBlockIds.reduce((hash, id) => {
      const cachedWorkflowBlock = workflowBlockEndpoints.getWorkflowBlock.select(id)({
        dashboardApi,
      }).data;
      if (!cachedWorkflowBlock) {
        return hash;
      }
      return {
        ...hash,
        [id]: cachedWorkflowBlock.data,
      };
    }, {});
  },
);

const getSchemaForFieldPath = createSelector(
  [getPlatformObjectJsonSchema, getPrimitiveUtilities],
  (platformObjectJsonSchema, primitiveUtilities) => {
    return (path: string, initialSchema: JsonSchema) => {
      return getSchemaForFieldPathStoreless({
        path,
        initialSchema,
        platformObjectJsonSchema,
        primitiveUtilities,
      });
    };
  },
);

export const useSchemaForFieldPath = () =>
  useSelector((state: RootState) => getSchemaForFieldPath(state));
